import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { IconButton } from '../Button';
import { XMarkIcon } from '../Icons/solid';
import { cn } from '../utils/classes';
import { transitions } from './config/transitions';

/**
 * Primitive — for use inside arch-ui only. Applications must use `arch-ui/src/Modal`,
 * which composes these parts with the standard responsive chrome. See `./index.ts`.
 */

/**
 * When true, marks this modal's portal container as `inert`,
 * prevents outside-click dismissal, and disables the escape key handler.
 */
export const InertModalContext = React.createContext(false);

/**
 * Context for the modal content element.
 * It is meant to be used to scope portals to prevent scroll/focus trap issues.
 * @see {../Combobox/Combobox.tsx} for example usage
 */
export const ModalContentContext = React.createContext(undefined);
const maxWithOptions = {
  xs: 'max-w-xs',
  sm: 'w-[25rem]',
  // 400px
  md: 'w-[32rem]',
  // 512px
  lg: 'w-[50rem]',
  // 800px
  xl: 'max-w-144',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  none: 'max-w-none',
  /**
   * Tailwinds percentage based width options
   * Reference: https://tailwindcss.com/docs/width#using-a-percentage
   */
  '1/2': 'max-w-1/2',
  '1/3': 'max-w-1/3',
  '2/3': 'max-w-2/3',
  '1/4': 'max-w-1/4',
  '3/4': 'max-w-3/4',
  '1/5': 'max-w-1/5',
  '2/5': 'max-w-2/5',
  '3/5': 'max-w-3/5',
  '4/5': 'max-w-4/5',
  '1/6': 'max-w-1/6',
  '5/6': 'max-w-5/6',
  full: 'max-w-full'
};
const getMaxWidth = maxWidth => maxWithOptions[maxWidth];
export const ModalBaseRoot = t0 => {
  const $ = _c(56);
  const {
    children,
    className,
    "data-cy": dataCy,
    disableEscapeKey,
    fullScreen,
    fullWidth,
    maxWidth,
    outsidePad,
    roundBorders,
    zIndex,
    transition: t1,
    onClose,
    open,
    initialFocus
  } = t0;
  const transition = t1 === undefined ? "none" : t1;
  const hasOverlay = !fullScreen || outsidePad;
  const inertModal = useContext(InertModalContext);
  const [contentElement, setContentElement] = useState(null);
  let t2;
  if ($[0] !== inertModal || $[1] !== onClose) {
    t2 = isOpen => {
      if (!isOpen && !inertModal) {
        onClose();
      }
    };
    $[0] = inertModal;
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleOpenChange = t2;
  let t3;
  if ($[3] !== disableEscapeKey || $[4] !== inertModal) {
    t3 = event => {
      if (disableEscapeKey || inertModal) {
        event.preventDefault();
      }
    };
    $[3] = disableEscapeKey;
    $[4] = inertModal;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleEscapeKeyDown = t3;
  let t4;
  if ($[6] !== inertModal) {
    t4 = event_0 => {
      if (inertModal) {
        event_0.preventDefault();
        return;
      }
      const target = event_0.target;
      if (!target.closest("[data-modal-portal]")) {
        event_0.preventDefault();
      }
    };
    $[6] = inertModal;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleOutsideInteraction = t4;
  let t5;
  if ($[8] !== inertModal || $[9] !== initialFocus) {
    t5 = event_1 => {
      if (inertModal) {
        event_1.preventDefault();
      } else {
        if (initialFocus?.current) {
          event_1.preventDefault();
          initialFocus.current.focus();
        }
      }
    };
    $[8] = inertModal;
    $[9] = initialFocus;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleOpenAutoFocus = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useLayoutEffect(_temp2, t6);
  let t7;
  let t8;
  if ($[12] !== open) {
    t7 = () => {
      if (!open) {
        return;
      }
      const clearBodyPointerEventsLock = _temp3;
      const observer = new MutationObserver(clearBodyPointerEventsLock);
      observer.observe(document.body, {
        attributeFilter: ["style"],
        attributes: true
      });
      clearBodyPointerEventsLock();
      return () => {
        observer.disconnect();
        document.body.style.removeProperty("pointer-events");
      };
    };
    t8 = [open];
    $[12] = open;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  const t9 = inertModal || undefined;
  let t10;
  if ($[15] !== zIndex) {
    t10 = {
      zIndex
    };
    $[15] = zIndex;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== outsidePad) {
    t11 = cn("fixed inset-0 z-modal flex items-center justify-center", {
      "p-8": outsidePad
    });
    $[17] = outsidePad;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== hasOverlay) {
    t12 = hasOverlay && <DialogPrimitive.Overlay asChild={true}><ModalBaseOverlay /></DialogPrimitive.Overlay>;
    $[19] = hasOverlay;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      pointerEvents: "auto"
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = transitions[transition];
  let t15;
  if ($[22] !== className || $[23] !== fullScreen || $[24] !== fullWidth || $[25] !== maxWidth || $[26] !== t14) {
    let t16;
    if ($[28] !== maxWidth) {
      t16 = maxWidth && getMaxWidth(maxWidth);
      $[28] = maxWidth;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    t15 = cn("flex flex-col max-h-full", t14, {
      "w-full": fullWidth,
      "h-full w-full": fullScreen
    }, t16, className);
    $[22] = className;
    $[23] = fullScreen;
    $[24] = fullWidth;
    $[25] = maxWidth;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = contentElement ?? undefined;
  let t17;
  if ($[30] !== outsidePad || $[31] !== roundBorders) {
    t17 = cn("flex grow transform flex-col overflow-hidden z-modal bg-white", {
      "shadow-xl": outsidePad,
      "rounded-lg": roundBorders
    });
    $[30] = outsidePad;
    $[31] = roundBorders;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== children || $[34] !== t17) {
    t18 = <div className={t17}>{children}</div>;
    $[33] = children;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t16 || $[37] !== t18) {
    t19 = <ModalContentContext.Provider value={t16}>{t18}</ModalContentContext.Provider>;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== dataCy || $[40] !== handleEscapeKeyDown || $[41] !== handleOpenAutoFocus || $[42] !== handleOutsideInteraction || $[43] !== t15 || $[44] !== t19) {
    t20 = <DialogPrimitive.Content ref={setContentElement} aria-describedby={undefined} data-cy={dataCy} onEscapeKeyDown={handleEscapeKeyDown} onPointerDownOutside={handleOutsideInteraction} onInteractOutside={handleOutsideInteraction} onOpenAutoFocus={handleOpenAutoFocus} style={t13} className={t15}>{t19}</DialogPrimitive.Content>;
    $[39] = dataCy;
    $[40] = handleEscapeKeyDown;
    $[41] = handleOpenAutoFocus;
    $[42] = handleOutsideInteraction;
    $[43] = t15;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t20 || $[50] !== t9) {
    t21 = <DialogPrimitive.Portal><div data-modal-portal="" inert={t9} style={t10} className={t11}>{t12}{t20}</div></DialogPrimitive.Portal>;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t20;
    $[50] = t9;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== handleOpenChange || $[53] !== open || $[54] !== t21) {
    t22 = <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>{t21}</DialogPrimitive.Root>;
    $[52] = handleOpenChange;
    $[53] = open;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
};
export const ModalBaseCloseButton = t0 => {
  const $ = _c(2);
  const {
    onClose
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = <IconButton icon={XMarkIcon} onClick={onClose} size="sm" color="secondary" variant="text" />;
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const ModalBaseContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex h-full grow flex-col overflow-auto px-6", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-testid="modal-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const ModalBaseFooter = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      topBorder: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const topBorder = t1 === undefined ? false : t1;
  const t2 = topBorder && "border-t border-gray-200";
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("flex w-full items-center justify-end space-x-2 px-5 pt-4 pb-6 sm:pb-4", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t3) {
    t4 = <div data-testid="modal-footer" {...props} className={t3} />;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const ModalBaseCloseIcon = t0 => {
  const $ = _c(3);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <XMarkIcon className="h-6 w-6" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onClick) {
    t2 = <button type="button" className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" onClick={onClick} aria-label="Close Overlay">{t1}</button>;
    $[1] = onClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const ModalBaseHeader = t0 => {
  const $ = _c(21);
  let children;
  let className;
  let onClose;
  let props;
  let rightContent;
  let t1;
  if ($[0] !== t0) {
    ({
      bottomBorder: t1,
      className,
      children,
      onClose,
      rightContent,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClose;
    $[4] = props;
    $[5] = rightContent;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    onClose = $[3];
    props = $[4];
    rightContent = $[5];
    t1 = $[6];
  }
  const bottomBorder = t1 === undefined ? false : t1;
  let t2;
  if ($[7] !== bottomBorder || $[8] !== className || $[9] !== onClose) {
    t2 = cn("relative w-full flex flex-col py-5 px-4 gap-2", {
      "pr-16": onClose,
      "border-b border-gray-200": bottomBorder
    }, className);
    $[7] = bottomBorder;
    $[8] = className;
    $[9] = onClose;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== onClose) {
    t3 = onClose && <ModalBaseCloseIcon onClick={onClose} />;
    $[11] = onClose;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== rightContent || $[14] !== t3) {
    t4 = <div className="absolute flex items-center gap-3 right-5 top-4">{rightContent}{t3}</div>;
    $[13] = rightContent;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== children || $[17] !== props || $[18] !== t2 || $[19] !== t4) {
    t5 = <div data-testid="modal-header" className={t2} {...props}>{children}{t4}</div>;
    $[16] = children;
    $[17] = props;
    $[18] = t2;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};
export const ModalBaseOverlay = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("fixed inset-0 bg-black opacity-30", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div {...props} aria-hidden="true" className={t1} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const ModalBaseTitle = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-lg font-medium text-gray-900 leading-6", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Title className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const ModalBaseSubTitle = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm font-normal leading-5 text-gray-500", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Description asChild={true}><h3 className={t1} {...props} /></DialogPrimitive.Description>;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp() {
  const root = document.getElementById("root");
  if (root) {
    requestAnimationFrame(() => root.removeAttribute("aria-hidden"));
  }
}
function _temp2() {
  return _temp;
}
function _temp3() {
  if (document.body.style.pointerEvents === "none") {
    document.body.style.removeProperty("pointer-events");
  }
}